/**
 * Vanilla JS custom element based on `zoom.ts` behavior.
 *
 * Usage:
 * <image-zoom
 *   thumb-src="/img/thumb.jpg"
 *   full-src="/img/full.jpg"
 *   mobile-src="/img/mobile.jpg"
 *   alt="Product image">
 * </image-zoom>
 */

const DESKTOP_BREAKPOINT = 1024;

const zoomTemplate = document.createElement('template');
zoomTemplate.innerHTML = `
  <style>
    :host { display: block; position: relative; }
    .g4-quiz-results__image-with-zoom__wrapper { position: relative; cursor: zoom-in; }
    .g4-quiz-results__image-with-zoom__thumb { display: block; width: 100%; height: auto; }
    .g4-quiz-results__image-with-zoom__mobile { display: none; width: 100%; height: auto; }
    .g4-quiz-results__image-with-zoom__lens {
      position: absolute;
      left: 0;
      top: 0;
      width: 50%;
      height: 50%;
      border: 1px solid #d8d0c8;
      border-radius: 6px;
      pointer-events: none;
      opacity: 0;
      z-index: -1;
      transition: opacity 0.3s ease;
    }
    .g4-quiz-results__image-with-zoom__zoomed-in {
      position: absolute;
      top: 50%;
      left: calc(100% + 16px);
      width: 300%;
      height: 300%;
      transform: translateY(-50%);
      background-color: #fff;
      background-repeat: no-repeat;
      pointer-events: none;
      opacity: 0;
      z-index: -1;
      box-shadow: 0 0 10px #7b7b7b;
      border-radius: 14px;
      transition: opacity 0.3s ease;
    }
    @media (max-width: 1023px) {
      .g4-quiz-results__image-with-zoom__thumb { display: none; }
      .g4-quiz-results__image-with-zoom__mobile { display: block; }
      .g4-quiz-results__image-with-zoom__lens,
      .g4-quiz-results__image-with-zoom__zoomed-in { display: none !important; }
    }
  </style>

  <div class="g4-quiz-results__image-with-zoom__wrapper">
    <img class="g4-quiz-results__image-with-zoom__thumb" alt="" />
    <img class="g4-quiz-results__image-with-zoom__mobile" alt="" />
    <div class="g4-quiz-results__image-with-zoom__lens"></div>
    <div class="g4-quiz-results__image-with-zoom__zoomed-in"></div>
  </div>
`;

class ImageZoom extends HTMLElement {
	constructor() {
		super();
		this.attachShadow({ mode: 'open' });
		this.shadowRoot.appendChild(zoomTemplate.content.cloneNode(true));

		this.thumbEl = this.shadowRoot.querySelector('.g4-quiz-results__image-with-zoom__thumb');
		this.lensEl = this.shadowRoot.querySelector('.g4-quiz-results__image-with-zoom__lens');
		this.zoomedInEl = this.shadowRoot.querySelector('.g4-quiz-results__image-with-zoom__zoomed-in');
		this.mobileEl = this.shadowRoot.querySelector('.g4-quiz-results__image-with-zoom__mobile');

		this.zoomFactorX = 0;
		this.zoomFactorY = 0;

		this.onMouseMove = this.moveLens.bind(this);
		this.onResize = this.setImageSrc.bind(this);
		this.onMouseEnter = () => this.handleZoomVisibility(true);
		this.onMouseLeave = () => this.handleZoomVisibility(false);
	}

	static get observedAttributes() {
		return ['thumb-src', 'full-src', 'mobile-src', 'alt'];
	}

	connectedCallback() {
		this.setImageSrc();
		this.setAltText();

		this.addEventListener('mouseenter', this.onMouseEnter);
		this.addEventListener('mouseleave', this.onMouseLeave);
		this.addEventListener('mousemove', this.onMouseMove);
		window.addEventListener('resize', this.onResize);
	}

	disconnectedCallback() {
		this.removeEventListener('mouseenter', this.onMouseEnter);
		this.removeEventListener('mouseleave', this.onMouseLeave);
		this.removeEventListener('mousemove', this.onMouseMove);
		window.removeEventListener('resize', this.onResize);
	}

	attributeChangedCallback(name) {
		if (name === 'alt') this.setAltText();
		if (name === 'thumb-src' || name === 'mobile-src') this.setImageSrc();
	}

	get thumbSrc() {
		return this.getAttribute('thumb-src') || '';
	}

	get fullSrc() {
		return this.getAttribute('full-src') || '';
	}

	get mobileSrc() {
		return this.getAttribute('mobile-src') || this.thumbSrc;
	}

	setAltText() {
		const alt = this.getAttribute('alt') || '';
		if (this.thumbEl) this.thumbEl.alt = alt;
		if (this.mobileEl) this.mobileEl.alt = alt;
	}

	setImageSrc() {
		if (!this.thumbEl || !this.mobileEl) return;

		if (window.innerWidth < DESKTOP_BREAKPOINT && !this.mobileEl.src) {
			this.mobileEl.src = this.mobileSrc;
		} else if (!this.thumbEl.src) {
			this.thumbEl.src = this.thumbSrc;
		}
	}

	handleZoomVisibility(show) {
		const opacity = show ? '1' : '0';
		const zIndex = show ? '5' : '-1';

		this.lensEl.style.opacity = opacity;
		this.zoomedInEl.style.opacity = opacity;
		this.zoomedInEl.style.zIndex = zIndex;
	}

	getCursorPosition(event) {
		const imageRect = this.thumbEl.getBoundingClientRect();
		const x = event.clientX - imageRect.left;
		const y = event.clientY - imageRect.top;
		return { x, y };
	}

	calculateZoomFactors() {
		this.zoomFactorX = this.zoomedInEl.offsetWidth / this.lensEl.offsetWidth;
		this.zoomFactorY = this.zoomedInEl.offsetHeight / this.lensEl.offsetHeight;
	}

	moveLens(event) {
		if (!this.fullSrc) return;

		const cursorPosition = this.getCursorPosition(event);
		let x = cursorPosition.x - this.lensEl.offsetWidth / 2;
		let y = cursorPosition.y - this.lensEl.offsetHeight / 2;

		const thumbW = this.thumbEl.width || this.thumbEl.offsetWidth;
		const thumbH = this.thumbEl.height || this.thumbEl.offsetHeight;
		x = Math.max(0, Math.min(x, thumbW - this.lensEl.offsetWidth));
		y = Math.max(0, Math.min(y, thumbH - this.lensEl.offsetHeight));

		this.lensEl.style.left = `${x}px`;
		this.lensEl.style.top = `${y}px`;

		this.calculateZoomFactors();

		this.zoomedInEl.style.backgroundImage = `url(${this.fullSrc})`;
		this.zoomedInEl.style.backgroundPosition = `-${x * this.zoomFactorX}px -${y * this.zoomFactorY}px`;
		this.zoomedInEl.style.backgroundSize = `${thumbW * this.zoomFactorX}px ${thumbH * this.zoomFactorY}px`;
	}
}

if (!customElements.get('image-zoom')) {
	customElements.define('image-zoom', ImageZoom);
}
